import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import { HTMLAttributes, ReactNode, forwardRef } from "react";
import { NotificationType } from "@common/helpers/NotificationManager";
import { __ } from "@common/helpers/i18n";
import { AlertProps } from "@common/types/elements/text/Alert";
import { cn } from "@app/lib/utils";

type FeedbackTone =
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "default"
  | "muted";

const variantMap = {
  success: "success",
  danger: "danger",
  warning: "warning",
  info: "info",
  light: "muted",
  gray: "muted",
  "outline-success": "success",
  "outline-danger": "danger",
  "outline-warning": "warning",
  "outline-info": "info",
  "outline-light": "muted",
  default: "default",
} as const;

const notificationToneMap: Record<NotificationType["type"], FeedbackTone> = {
  success: "success",
  error: "danger",
  warning: "warning",
  info: "info",
  default: "default",
};

const feedbackTone = {
  success: {
    Icon: IconCheck,
    accent: "bg-success",
    panel: "border-success/20 bg-card",
    icon: "bg-success/10 text-success ring-success/15",
  },
  danger: {
    Icon: IconAlertCircle,
    accent: "bg-destructive",
    panel: "border-destructive/20 bg-card",
    icon: "bg-destructive/10 text-destructive ring-destructive/15",
  },
  warning: {
    Icon: IconAlertTriangle,
    accent: "bg-warning",
    panel: "border-warning/25 bg-card",
    icon: "bg-warning/10 text-warning ring-warning/15",
  },
  info: {
    Icon: IconInfoCircle,
    accent: "bg-info",
    panel: "border-info/20 bg-card",
    icon: "bg-info/10 text-info ring-info/15",
  },
  default: {
    Icon: IconInfoCircle,
    accent: "bg-muted-foreground",
    panel: "border-border bg-card",
    icon: "bg-muted text-muted-foreground ring-border",
  },
  muted: {
    Icon: IconInfoCircle,
    accent: "bg-muted-foreground/50",
    panel: "border-border bg-muted/20",
    icon: "bg-muted text-muted-foreground ring-border",
  },
} satisfies Record<
  FeedbackTone,
  {
    Icon: typeof IconInfoCircle;
    accent: string;
    panel: string;
    icon: string;
  }
>;

const outlinePanelClassName = {
  "outline-success": "border-success/30 bg-transparent",
  "outline-danger": "border-destructive/30 bg-transparent",
  "outline-warning": "border-warning/30 bg-transparent",
  "outline-info": "border-info/30 bg-transparent",
  "outline-light": "border-border bg-transparent text-muted-foreground",
} satisfies Partial<Record<NonNullable<AlertProps["variant"]>, string>>;

const paddingClassName = {
  none: "",
  small: "px-3 py-2",
  medium: "p-4",
  large: "p-5",
};

const alertActionClassName = {
  success:
    "[&_[data-action-control=true]]:border-success [&_[data-action-control=true]]:bg-success [&_[data-action-control=true]]:text-success-foreground [&_[data-action-control=true]:hover]:border-success/90 [&_[data-action-control=true]:hover]:bg-success/90",
  danger:
    "[&_[data-action-control=true]]:border-destructive [&_[data-action-control=true]]:bg-destructive [&_[data-action-control=true]]:text-destructive-foreground [&_[data-action-control=true]:hover]:border-destructive/90 [&_[data-action-control=true]:hover]:bg-destructive/90",
  warning:
    "[&_[data-action-control=true]]:border-warning [&_[data-action-control=true]]:bg-warning [&_[data-action-control=true]]:text-warning-foreground [&_[data-action-control=true]:hover]:border-warning/90 [&_[data-action-control=true]:hover]:bg-warning/90",
  info: "[&_[data-action-control=true]]:border-info [&_[data-action-control=true]]:bg-info [&_[data-action-control=true]]:text-info-foreground [&_[data-action-control=true]:hover]:border-info/90 [&_[data-action-control=true]:hover]:bg-info/90",
  default:
    "[&_[data-action-control=true]]:border-foreground/20 [&_[data-action-control=true]]:bg-foreground [&_[data-action-control=true]]:text-background [&_[data-action-control=true]:hover]:bg-foreground/90",
  muted:
    "[&_[data-action-control=true]]:border-foreground/20 [&_[data-action-control=true]]:bg-foreground [&_[data-action-control=true]]:text-background [&_[data-action-control=true]:hover]:bg-foreground/90",
} satisfies Record<FeedbackTone, string>;

type FeedbackMessageProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  action?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode | false;
  message?: ReactNode;
  mode?: "alert" | "toast";
  onClose?: () => void;
  padding?: NonNullable<AlertProps["padding"]>;
  showIcon?: boolean;
  title?: ReactNode;
  tone?: FeedbackTone;
};

export const FeedbackMessage = forwardRef<HTMLDivElement, FeedbackMessageProps>(
  function FeedbackMessage(
    {
      action,
      children,
      className,
      icon,
      message,
      mode = "alert",
      onClose,
      padding = "medium",
      role,
      showIcon = mode === "toast",
      title,
      tone = "default",
      ...rest
    },
    ref,
  ) {
    const toneConfig = feedbackTone[tone];
    const ToneIcon = toneConfig.Icon;
    const usesDefaultIcon = icon !== false && !icon && showIcon;
    const iconNode =
      icon === false
        ? null
        : icon || (showIcon ? <ToneIcon size={18} stroke={2} /> : null);
    const content =
      title || message ? (
        <div
          className={cn(
            "min-w-0 flex-1",
            mode === "toast" && "flex min-h-9 flex-col justify-center",
          )}
        >
          {title && (
            <p className="text-sm font-extrabold leading-6 text-foreground">
              {title}
            </p>
          )}
          {message && (
            <p
              className={cn(
                "text-sm font-medium leading-6 text-muted-foreground",
                title && "mt-0.5",
              )}
            >
              {message}
            </p>
          )}
          {children && (
            <div className={cn(title || message ? "mt-2" : "")}>{children}</div>
          )}
        </div>
      ) : (
        children
      );

    const body = (
      <>
        {iconNode && usesDefaultIcon ? (
          <span
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-md ring-1 [&_svg]:h-5 [&_svg]:w-5",
              mode !== "toast" && "mt-0.5",
              toneConfig.icon,
            )}
          >
            {iconNode}
          </span>
        ) : (
          iconNode
        )}
        {content}
        {action && (
          <div
            className={cn(
              "shrink-0",
              mode !== "toast" &&
                "drop-shadow-sm [&_[data-action-control=true]]:font-extrabold [&_[data-action-control=true]]:shadow-none",
              mode !== "toast" && alertActionClassName[tone],
              mode === "toast"
                ? "ms-auto"
                : "basis-full sm:ms-auto sm:basis-auto",
            )}
          >
            {action}
          </div>
        )}
        {onClose && (
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={onClose}
            aria-label={__("Close message")}
          >
            <IconX size={17} stroke={2} aria-hidden="true" />
          </button>
        )}
      </>
    );

    if (mode === "toast") {
      return (
        <div
          ref={ref}
          role={role ?? (tone === "danger" ? "alert" : "status")}
          className={cn(
            "pointer-events-auto w-full overflow-hidden rounded-lg border text-start text-card-foreground shadow-medium",
            toneConfig.panel,
            className,
          )}
          {...rest}
        >
          <span className={cn("block h-0.5 w-full", toneConfig.accent)} />
          <div className="flex min-w-0 items-center gap-3 px-3.5 py-3">
            {body}
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        role={role ?? "alert"}
        className={cn(
          "relative flex w-full min-w-0 flex-wrap items-start gap-3 rounded-lg border text-start text-sm leading-6 text-foreground sm:flex-nowrap",
          toneConfig.panel,
          paddingClassName[padding],
          className,
        )}
        {...rest}
      >
        {body}
      </div>
    );
  },
);

type AlertComponentProps = Omit<AlertProps, "icon"> & {
  action?: ReactNode;
  icon?: ReactNode | false;
  onClose?: () => void;
  title?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, "title">;

export const Alert = forwardRef<HTMLDivElement, AlertComponentProps>(
  function Alert(
    { variant, icon, children, className, padding = "medium", title, ...rest },
    ref,
  ) {
    const outlineClassName =
      variant && variant in outlinePanelClassName
        ? outlinePanelClassName[variant as keyof typeof outlinePanelClassName]
        : undefined;

    return (
      <FeedbackMessage
        ref={ref}
        className={cn(outlineClassName, className)}
        icon={icon}
        padding={padding}
        title={title}
        tone={variantMap[variant || "default"]}
        {...rest}
      >
        {children}
      </FeedbackMessage>
    );
  },
);

type ToastProps = {
  action?: ReactNode;
  className?: string;
  message?: ReactNode;
  onClose?: () => void;
  title: ReactNode;
  type?: NotificationType["type"];
};

export const Toast = forwardRef<HTMLDivElement, ToastProps>(function Toast(
  { action, className, message, title, type = "default", ...rest },
  ref,
) {
  return (
    <FeedbackMessage
      action={action}
      ref={ref}
      className={className}
      message={message}
      mode="toast"
      title={title}
      tone={notificationToneMap[type]}
      {...rest}
    />
  );
});
